// SearchWidget — overlapping search pill from the Voyo web design.
// POSTs /searches and navigates to /search/:id on submit.
// Origin/dest use AirportAutocomplete (live /airports/search).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, extractApiError } from '@/lib/api';
import { AirportAutocomplete, type AirportOption } from './AirportAutocomplete';
import { Spinner } from './primitives';
import { useIsMobile } from '@/hooks/useIsMobile';

type TripType = 'round' | 'one' | 'multi' | 'hotel' | 'bundle';

const TRIP_TYPES: Array<[TripType, string]> = [
  ['round',  'Round-trip'],
  ['one',    'One-way'],
  ['multi',  'Multi-city'],
  ['hotel',  'Hotels'],
  ['bundle', '✦ Flight+Hotel'],
];

interface SearchCreateResponse {
  data?: {
    searchId?: string;
    id?: string;
  };
}

export function SearchWidget() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tripType, setTripType] = useState<TripType>('round');
  const [origin, setOrigin] = useState<AirportOption | null>(null);
  const [dest, setDest]     = useState<AirportOption | null>(null);
  const [depart, setDepart] = useState('');
  const [returnDate, setReturn] = useState('');
  const [pax, setPax]       = useState('1 adult · Economy');

  const createSearch = useMutation({
    mutationFn: async () => {
      if (!origin || !dest) throw new Error('Please select origin and destination');
      if (!depart) throw new Error('Please select a departure date');

      const body = {
        origin: origin.iata,
        destination: dest.iata,
        departDate: depart,
        returnDate: tripType === 'round' ? returnDate : undefined,
        adults: 1,
        cabinClass: 'economy',
        tripType,
      };

      const res = await api.post<SearchCreateResponse>('/searches', body);
      const searchId = res.data.data?.searchId ?? res.data.data?.id;
      if (!searchId) throw new Error('No search ID returned');
      return searchId;
    },
    onSuccess: (searchId: string) => {
      navigate(`/search/${searchId}`);
    },
    onError: (err: unknown) => {
      const apiErr = extractApiError(err);
      toast.error(apiErr.message ?? 'Search failed');
    },
  });

  const handleSearch = () => {
    createSearch.mutate();
  };

  const T = {
    surface: '#131C30',
    bg:      '#0A0F1C',
    ink:     '#F4F3F0',
    inkDim:  '#8C98B0',
    primary: '#E8441A',
    line:    'rgba(255,255,255,0.07)',
  };

  return (
    <div style={{
      background: T.surface,
      borderRadius: 28,
      padding: 'clamp(16px, 4vw, 24px)',
      border: `1px solid rgba(255,255,255,0.07)`,
      boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
    }}>
      {/* Trip type tabs — wrap to a second row on mobile so all 5 stay visible.
          Tried overflowX:'auto' first; not robust because off-screen tabs are
          invisible to users without a horizontal-scroll affordance. Wrapping is
          cleaner and shows the full set. */}
      <div style={{
        display: 'flex',
        gap: 6,
        marginBottom: 18,
        flexWrap: 'wrap',
      }}>
        {TRIP_TYPES.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTripType(k)}
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: tripType === k ? T.bg : 'transparent',
              color: tripType === k ? T.ink : T.inkDim,
              fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
              fontSize: 13,
              fontWeight: 500,
              transition: 'all .15s',
              whiteSpace: 'nowrap',
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Fields grid — on mobile, collapse to a single column so each input
          (FROM, TO, DEPART, RETURN, TRAVELERS) gets the full width and stays
          readable. Search button stretches across the bottom. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? '1fr'
          : tripType === 'round'
            ? '1.2fr 1.2fr 1fr 1fr 0.8fr 140px'
            : '1.2fr 1.2fr 1fr 0.8fr 140px',
        gap: 1,
        background: T.line,
        borderRadius: 18,
        overflow: 'hidden',
        border: `1px solid ${T.line}`,
      }}>
        {/* Origin */}
        <AirportAutocomplete
          label="FROM"
          placeholder="BLR · Bangalore"
          value={origin}
          onChange={setOrigin}
        />

        {/* Destination */}
        <AirportAutocomplete
          label="TO"
          placeholder="DPS · Bali"
          value={dest}
          onChange={setDest}
        />

        {/* Depart */}
        <div style={{ padding: '14px 18px', background: T.surface }}>
          <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 10, color: T.inkDim, letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>DEPART</div>
          <input
            type="date"
            value={depart}
            onChange={(e) => setDepart(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
              fontSize: 16, fontWeight: 600, color: depart ? T.ink : T.inkDim,
              width: '100%', caretColor: '#E8441A',
            }}
          />
        </div>

        {/* Return (round only) */}
        {tripType === 'round' && (
          <div style={{ padding: '14px 18px', background: T.surface }}>
            <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 10, color: T.inkDim, letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>RETURN</div>
            <input
              type="date"
              value={returnDate}
              onChange={(e) => setReturn(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
                fontSize: 16, fontWeight: 600, color: returnDate ? T.ink : T.inkDim,
                width: '100%', caretColor: '#E8441A',
              }}
            />
          </div>
        )}

        {/* Travelers */}
        <div style={{ padding: '14px 18px', background: T.surface }}>
          <div style={{ fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif', fontSize: 10, color: T.inkDim, letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>TRAVELERS</div>
          <input
            type="text"
            value={pax}
            onChange={(e) => setPax(e.target.value)}
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
              fontSize: 15, fontWeight: 600, color: T.ink,
              width: '100%',
            }}
          />
        </div>

        {/* Search CTA */}
        <button
          onClick={handleSearch}
          disabled={createSearch.isPending}
          style={{
            background: '#E8441A',
            color: '#0A0F1C',
            border: 'none',
            cursor: createSearch.isPending ? 'wait' : 'pointer',
            fontFamily: '"Inter Variable", "Inter", system-ui, sans-serif',
            fontSize: 15,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            transition: 'background .15s',
            opacity: createSearch.isPending ? 0.8 : 1,
            // On desktop the button sits in its own grid track (140px wide); on
            // mobile it stacks below the last input and needs its own vertical
            // padding to render as a button rather than a 0-height pill.
            padding: isMobile ? '16px 0' : 0,
          }}
        >
          {createSearch.isPending ? (
            <Spinner />
          ) : (
            'Search →'
          )}
        </button>
      </div>
    </div>
  );
}
